// React&NextJS
import React, { useEffect } from 'react'
// 3rd party libraries
import { motion } from 'framer-motion'
// Services&Helper functions
import helperFunctions from '../../../../utils/helperFunctions'
// Types
import { Memory } from '../../../../types/types'
// Styles
import s from './Memories.module.scss'
// Components
import { Button } from '../../../core/button/Button'
import { ButtonMap } from '../../buttonMap/ButtonMap'
import { Image } from '../../../core/image/Image'
import { Text } from '../../../core/text/Text'
import { usePlausible } from 'next-plausible'
import { WhiteBox } from '../../../core/whiteBox/WhiteBox'

type FollowedMem = Memory & {
    readOnly: boolean
}

type MemoriesProps = {
    memories: Array<Memory>
    openReqModal: Function
    unfollowMemory?: Function
    fetchFollowData?: Function
    moreFollowing?: Boolean
    followed?: Array<FollowedMem>
    fadeIn?: Boolean
}

export default function UserMemories({
    memories,
    unfollowMemory,
    fetchFollowData,
    moreFollowing,
    followed = [],
    fadeIn = true,
}: MemoriesProps) {
    const plausible = usePlausible()

    useEffect(() => {
        const handleScroll = () => {
            let documentHeight = document.body.scrollHeight
            let currentScroll = window.scrollY + window.innerHeight
            // When the user is [modifier]px from the bottom, fire the event.
            let modifier = 50
            if (currentScroll + modifier > documentHeight) {
                moreFollowing && fetchFollowData()
                modifier -= 200
            }
        }
        handleScroll()
        window.addEventListener('scroll', handleScroll)
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    })

    const defaultText = () => (
        <div className={s.default}>
            <WhiteBox>
                <Text variant="h4" align="center">
                    Stofna minningarsíðu
                </Text>
                <Text variant="p" align="center">
                    Minningar.is er vefur þar sem minningin lifir. Þú getur
                    stofnað minningasíðu um látinn ástvin og deilt henni með
                    öðrum.
                </Text>
                <div className={s.buttons}>
                    <Button
                        to="/memories/form"
                        variant="ghost"
                        onClick={() =>
                            plausible('account-stofnaMinningarsidu-button')
                        }
                    >
                        Stofna minningarsíðu
                    </Button>
                </div>
            </WhiteBox>
            <WhiteBox>
                <Text variant="h4" align="center">
                    Tilkynna andlát
                </Text>
                <Text variant="p" align="center">
                    Það er einfalt og gjaldfrjálst að tilkynna andlát á vefnum
                    en við það verður til minningarsíða á Minningar.is
                </Text>
                <div className={s.buttons}>
                    <Button
                        to="/memories/form?recent=true"
                        variant="ghost"
                        // onClick={() =>
                        //     plausible('account-stofnaMinningarsidu-button')
                        // }
                    >
                        Tilkynna andlát
                    </Button>
                </div>
            </WhiteBox>
        </div>
    )

    const container = {
        hidden: {
            opacity: 0,
        },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    }

    const item = {
        hidden: { opacity: 0, transform: 'translateY(200px)' },
        show: {
            transform: 'translateY(0px)',
            opacity: 1,
            transition: {
                duration: 0.6,
                type: 'tween',
                // repeat: Infinity,
            },
        },
    }
    return (
        <>
            {defaultText()}
            <motion.div
                className={s.memories}
                variants={container}
                initial={fadeIn ? 'hidden' : 'show'}
                animate="show"
            >
                {memories?.length > 0 && (
                    <Text variant="h4">Mínar minningarsíður</Text>
                )}
                {memories.map((m, i) => {
                    const birth = helperFunctions.getDate(m.birth)
                    const death = helperFunctions.getDate(m.death)

                    return (
                        <motion.div
                            key={i}
                            className={s.card}
                            id={m.mid}
                            variants={item}
                        >
                            <Image
                                className={s.card__image}
                                centerFace
                                src={m.profileImg}
                                alt=""
                            />

                            <div className={s.card__details}>
                                <div>
                                    <Text variant="h4">{m.fullName}</Text>
                                    <Text variant="date">
                                        {birth} - {death}
                                    </Text>
                                </div>

                                <ButtonMap className={s.card__details__buttons}>
                                    <Button
                                        variant="text"
                                        to={`/account/memories/${m.mid}`}
                                    >
                                        Breyta
                                    </Button>
                                    <Button
                                        variant="text"
                                        to={`/memories/${m.mid}`}
                                    >
                                        Skoða síðu
                                    </Button>
                                    {m.obituaries?.length > 0 && (
                                        <Button
                                            variant="text"
                                            to={`/account/memories/${m.mid}/obituaries`}
                                        >
                                            Minningargreinar
                                        </Button>
                                    )}
                                    {m.condolences?.length > 0 && (
                                        <Button
                                            variant="text"
                                            to={`/account/memories/${m.mid}/condolences`}
                                        >
                                            Kveðjur
                                        </Button>
                                    )}
                                    <Button
                                        variant="text"
                                        to={`/account/memories/${m.mid}/owners`}
                                    >
                                        Umsjón
                                    </Button>
                                </ButtonMap>
                            </div>
                        </motion.div>
                    )
                })}
                {followed?.length > 0 && (
                    <motion.div variants={item}>
                        <Text variant="h4">Síður sem ég fylgi</Text>
                    </motion.div>
                )}
                {followed.map((m, i) => {
                    const birth = helperFunctions.getDate(m.birth)
                    const death = helperFunctions.getDate(m.death)
                    return (
                        <motion.div
                            key={i}
                            className={s.card}
                            id={m.mid}
                            variants={item}
                        >
                            <Image
                                className={s.card__image}
                                centerFace
                                src={m.profileImg}
                                alt=""
                            />

                            <div className={s.card__details}>
                                <div>
                                    <Text variant="h4">{m.fullName}</Text>
                                    <Text variant="date">
                                        {birth} - {death}
                                    </Text>
                                </div>

                                <ButtonMap className={s.card__details__buttons}>
                                    <Button
                                        variant="text"
                                        to={`/memories/${m.mid}`}
                                    >
                                        Skoða síðu
                                    </Button>
                                    <Button
                                        variant="text"
                                        to={`/memories/${m.mid}/obituaries/form`}
                                    >
                                        Skrifa minningargrein
                                    </Button>
                                    <Button
                                        variant="text"
                                        onClick={() => unfollowMemory(m.mid)}
                                    >
                                        Hætta að fylgja
                                    </Button>
                                </ButtonMap>
                            </div>
                        </motion.div>
                    )
                })}
            </motion.div>
        </>
    )
}
